import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FilterValues } from '../components/shared/ListFilterBar'

export interface ListPageState extends FilterValues {
  // Pagination
  page: number
  perPage: number
  
  // Sorting  
  sortField: string
  sortDirection: 'asc' | 'desc'
  
  // Other common filters
  search: string
  [key: string]: any
}

interface UseListPageStateOptions {
  /** Initial state values */
  initialState?: Partial<ListPageState>
  /** Whether to sync state with URL parameters */
  syncWithURL?: boolean
  /** Debounce delay for search (ms) */
  searchDebounce?: number
  /** Default sort field */
  defaultSortField?: string
  /** Default per page */
  defaultPerPage?: number
}

interface UseListPageStateReturn {
  /** Current state */
  state: ListPageState
  /** Update a single field */
  updateField: (key: keyof ListPageState, value: any) => void
  /** Update multiple fields at once */
  updateFields: (updates: Partial<ListPageState>) => void
  /** Reset to initial state */
  reset: () => void
  /** Reset pagination to page 1 (useful when filters change) */
  resetPagination: () => void
}

export const useListPageState = ({
  initialState = {},
  syncWithURL = true,
  searchDebounce = 300,
  defaultSortField = 'id',
  defaultPerPage = 25
}: UseListPageStateOptions = {}): UseListPageStateReturn => {
  const location = useLocation()
  const navigate = useNavigate()
  
  // Parse initial state from URL if syncing
  const getInitialStateFromURL = useCallback(() => {
    if (!syncWithURL) return {}
    
    const params = new URLSearchParams(location.search)
    const urlState: Partial<ListPageState> = {}
    
    // Extract common parameters
    const page = params.get('page')
    const perPage = params.get('per_page') || params.get('perPage')
    const sortField = params.get('sort_field') || params.get('sortField')
    const sortDirection = params.get('sort_direction') || params.get('sortDirection')
    const search = params.get('search') || params.get('q')
    
    if (page) urlState.page = parseInt(page, 10)
    if (perPage) urlState.perPage = parseInt(perPage, 10)
    if (sortField) urlState.sortField = sortField
    if (sortDirection && (sortDirection === 'asc' || sortDirection === 'desc')) {
      urlState.sortDirection = sortDirection
    }
    if (search) urlState.search = search
    
    // Extract other parameters
    for (const [key, value] of params.entries()) {
      if (!['page', 'per_page', 'perPage', 'sort_field', 'sortField', 'sort_direction', 'sortDirection', 'search', 'q'].includes(key)) {
        urlState[key] = value
      }
    }
    
    return urlState
  }, [location.search, syncWithURL])

  // Initialize state
  const [state, setState] = useState<ListPageState>({
    page: 1,
    perPage: defaultPerPage,
    sortField: defaultSortField,
    sortDirection: 'asc',
    search: '',
    ...initialState,
    ...getInitialStateFromURL()
  })

  // Sync state to URL
  const syncToURL = useCallback((newState: ListPageState) => {
    if (!syncWithURL) return

    const params = new URLSearchParams()
    
    // Add non-default values to URL
    if (newState.page > 1) params.set('page', newState.page.toString())
    if (newState.perPage !== defaultPerPage) params.set('per_page', newState.perPage.toString())
    if (newState.sortField !== defaultSortField) params.set('sort_field', newState.sortField)
    if (newState.sortDirection !== 'asc') params.set('sort_direction', newState.sortDirection)
    if (newState.search) params.set('search', newState.search)
    
    // Add other fields
    Object.entries(newState).forEach(([key, value]) => {
      if (!['page', 'perPage', 'sortField', 'sortDirection', 'search'].includes(key) && value && value !== '') {
        if (typeof value === 'string' || typeof value === 'number') {
          params.set(key, value.toString())
        }
      }
    })

    const newSearch = params.toString()
    const currentSearch = location.search.replace('?', '')
    
    if (newSearch !== currentSearch) {
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      }, { replace: true })
    }
  }, [syncWithURL, defaultPerPage, defaultSortField, location.pathname, location.search, navigate])

  // Debounced search handler
  useEffect(() => {
    const timer = setTimeout(() => {
      syncToURL(state)
    }, state.search ? searchDebounce : 0)
    
    return () => clearTimeout(timer)
  }, [state, syncToURL, searchDebounce])

  const updateField = useCallback((key: keyof ListPageState, value: any) => {
    setState(prev => {
      const newState = { ...prev, [key]: value }
      
      // Reset pagination when filters change (but not when page itself changes)
      if (key !== 'page' && key !== 'perPage') {
        newState.page = 1
      }
      
      return newState
    })
  }, [])

  const updateFields = useCallback((updates: Partial<ListPageState>) => {
    setState(prev => {
      const newState = { ...prev, ...updates }
      
      // Reset pagination if any non-pagination fields changed
      const nonPaginationUpdates = Object.keys(updates).filter(key => !['page', 'perPage'].includes(key))
      if (nonPaginationUpdates.length > 0 && !updates.page) {
        newState.page = 1
      }
      
      return newState
    })
  }, [])

  const reset = useCallback(() => {
    const resetState = {
      page: 1,
      perPage: defaultPerPage,
      sortField: defaultSortField,
      sortDirection: 'asc' as const,
      search: '',
      ...initialState
    }
    setState(resetState)
  }, [initialState, defaultPerPage, defaultSortField])

  const resetPagination = useCallback(() => {
    updateField('page', 1)
  }, [updateField])

  return {
    state,
    updateField,
    updateFields,
    reset,
    resetPagination
  }
}

export default useListPageState